import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	FormHelperText,
	Typography,
} from '@mui/material';
import { Button, Modal } from 'components/ui/';
import range from 'lodash/range';
import without from 'lodash/without';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { vaxEditDataActions } from 'store/vaxEditDataSlice.js';
import { formActions } from 'store/vaxFormDataSlice';
import styled from 'styled-components';

const AvatarImage = styled.img`
	cursor: pointer;
	max-width: 100px;
	border-radius: 50%;
`;

export const avatars_f_baby = [12, 13];
export const avatars_f_1_2_years = [230, 231, 232, 233, 234];
export const avatars_f_3_4_years = [248, 249, 250, 251, 252];
export const avatars_f_5_6_years = [253, 254, 255, 256, 257];
export const avatars_f_7_12_years = [258, 259, 260, 261, 262, 263, 264];
export const avatars_f_13_18_years = [
	235, 236, 237, 238, 239, 240, 241, 242, 243, 244, 245, 246, 247,
];
export const avatars_f_19_29_years = range(14, 41);
export const avatars_f_30_39_years = range(42, 69);
export const avatars_f_40_49_years = range(71, 82);
export const avatars_f_50_59_years = range(83, 116);
export const avatars_f_60_69_years = range(117, 154);
export const avatars_f_70_79_years = [...range(155, 159), ...range(503, 512)];
export const avatars_f_80_89_years = range(164, 182);
export const avatars_f_90_years = range(183, 201);

export const avatars_m_baby = [10, 11];
export const avatars_m_1_2_years = [202, 203, 204, 205, 206];
export const avatars_m_3_4_years = [213, 214, 215, 216, 217];
export const avatars_m_5_6_years = [218, 219, 220, 221, 222];
export const avatars_m_7_12_years = [223, 224, 225, 226, 227, 228, 229];
export const avatars_m_13_18_years = [207, 208, 209, 210, 211, 212];
export const avatars_m_19_29_years = range(265, 292);
export const avatars_m_30_39_years = without(range(293, 331), 300);
export const avatars_m_40_49_years = range(332, 368);
export const avatars_m_50_59_years = range(369, 394);
export const avatars_m_60_69_years = without(range(395, 429), 411);
export const avatars_m_70_79_years = without(range(430, 454), 433);
export const avatars_m_80_89_years = range(455, 478);
export const avatars_m_90_years = range(479, 502);

const AvatarAccordion = ({ avatarIds, platzhalterString, callback }) => {
	const [expanded, setExpanded] = useState(null);
	return (
		<>
			{avatarIds.map((group, index) => {
				return (
					<Accordion
						key={'age_group' + index}
						disableGutters={true}
						expanded={expanded == index}
						onChange={() => {
							if (expanded == index) {
								setExpanded(null);
							} else {
								setExpanded(index);
							}
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls='panel1a-content'
							id='panel1a-header'
						>
							<Typography>{group.title}</Typography>
						</AccordionSummary>
						<AccordionDetails>
							{group.ids.map((id) => {
								return (
									<AvatarImage
										key={'id' + id}
										src={`${process.env.API_URL}v1/glob/files/${id}/avatar.png`}
										alt={platzhalterString}
										onClick={() => callback(id)}
									/>
								);
							})}
						</AccordionDetails>
					</Accordion>
				);
			})}
		</>
	);
};

AvatarAccordion.propTypes = {
	avatarIds: PropTypes.array,
	platzhalterString: PropTypes.string,
	callback: PropTypes.func,
};

const ChooseAvatarDialog = ({ internal, edit, formData, updateUser }) => {
	const [show, setShow] = useState(false);
	const [t] = useTranslation();
	const handleClose = () => setShow(false);
	const handleShow = () => setShow(true);
	const dispatch = useDispatch();
	const [sex, setSex] = useState(
		formData.sex == 'unknown' ? false : formData.sex
	);

	const maleAvatarIds = [
		{ title: t('impfschaeden.erfassen.saeugling'), ids: avatars_m_baby }, // baby
		{ title: t('master_data.age_group.1_2_jahre'), ids: avatars_m_1_2_years }, // 1-2 jahre
		{ title: t('master_data.age_group.3_4_jahre'), ids: avatars_m_3_4_years }, // 3-4 jahre
		{ title: t('master_data.age_group.5_6_jahre'), ids: avatars_m_5_6_years }, // 5-6 jahre
		{ title: t('master_data.age_group.7_12_jahre'), ids: avatars_m_7_12_years }, // 7-12 jahre
		{
			title: t('master_data.age_group.13_18_jahre'),
			ids: avatars_m_13_18_years,
		}, // 13-18 jahre
		{
			title: t('master_data.age_group.19_29_jahre'),
			ids: avatars_m_19_29_years,
		}, // 19-29 jahre
		{
			title: t('master_data.age_group.30_39_jahre'),
			ids: avatars_m_30_39_years,
		}, // 30-39 jahre
		{
			title: t('master_data.age_group.40_49_jahre'),
			ids: avatars_m_40_49_years,
		}, // 40-49 jahre
		{
			title: t('master_data.age_group.50_59_jahre'),
			ids: avatars_m_50_59_years,
		}, // 50-59 jahre
		{
			title: t('master_data.age_group.60_69_jahre'),
			ids: avatars_m_60_69_years,
		}, // 60-69 jahre
		{
			title: t('master_data.age_group.70_79_jahre'),
			ids: avatars_m_70_79_years,
		}, // 70-79 jahre
		{
			title: t('master_data.age_group.80_89_jahre'),
			ids: avatars_m_80_89_years,
		}, // 80-89 jahre
		// { title: t('master_data.age_group.>90_jahre'), ids: avatars_m_90_years }, // > 90 jahre
	];

	const femaleAvatarIds = [
		{ title: t('impfschaeden.erfassen.saeugling'), ids: avatars_f_baby }, //
		{ title: t('master_data.age_group.1_2_jahre'), ids: avatars_f_1_2_years }, // 1-2 jahre
		{ title: t('master_data.age_group.3_4_jahre'), ids: avatars_f_3_4_years }, //
		{ title: t('master_data.age_group.5_6_jahre'), ids: avatars_f_5_6_years }, //
		{ title: t('master_data.age_group.7_12_jahre'), ids: avatars_f_7_12_years }, //
		{
			title: t('master_data.age_group.13_18_jahre'),
			ids: avatars_f_13_18_years,
		}, //
		{
			title: t('master_data.age_group.19_29_jahre'),
			ids: avatars_f_19_29_years,
		}, // 19-29 jahre
		{
			title: t('master_data.age_group.30_39_jahre'),
			ids: avatars_f_30_39_years,
		}, // 30-39 jahre
		{
			title: t('master_data.age_group.40_49_jahre'),
			ids: avatars_f_40_49_years,
		}, // 40-49 jahre
		{
			title: t('master_data.age_group.50_59_jahre'),
			ids: avatars_f_50_59_years,
		}, // 50-59 jahre
		{
			title: t('master_data.age_group.60_69_jahre'),
			ids: avatars_f_60_69_years,
		}, // 60-69 jahre
		{
			title: t('master_data.age_group.70_79_jahre'),
			ids: avatars_f_70_79_years,
		}, // 70-79 jahre
		{
			title: t('master_data.age_group.80_89_jahre'),
			ids: avatars_f_80_89_years,
		}, // 80-89 jahre
		{ title: t('master_data.age_group.>90_jahre'), ids: avatars_f_90_years }, // > 90 jahre
	];

	const chooseAvatar = (id) => {
		if (internal) {
			updateUser({ avatar_glob_file_id: id });
		} else if (edit) {
			dispatch(
				vaxEditDataActions.update({ avatar_glob_file_id: id, edited: true })
			);
		} else {
			dispatch(formActions.update({ avatar_glob_file_id: id }));
		}

		handleClose();
	};
	if (!formData) {
		return null;
	}

	return (
		<>
			<Button variant='primary' onClick={handleShow}>
				{t('impfschaeden.erfassen.platzhalter_wahlen')}
			</Button>

			<Modal show={show} onHide={handleClose}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('impfschaeden.erfassen.waehle_einen_passenden_platzhalter')}
						{!(formData.sex != 'm' && formData?.sex != 'w') && (
							<FormHelperText
								style={{ fontSize: '.9rem', color: 'var(--bs-red)' }}
							>
								{t('impfschaeden.erfassen.alterskategorien_nur_hilfestellung')}
							</FormHelperText>
						)}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{(sex == 'm' || sex == 'w') && (
						<Button className='mb-2' onClick={() => setSex(false)}>
							{t('common.zurueck')}
						</Button>
					)}
					{sex != 'm' && sex != 'w' && (
						<div>
							<div
								className='d-flex justify-content-around mb-3'
								style={{ height: '100px' }}
							>
								<Button variant='primary' onClick={() => setSex('m')}>
									{t('impfschaeden.erfassen.maennliche_platzhalter')}
								</Button>
								<Button variant='primary' onClick={() => setSex('w')}>
									{t('impfschaeden.erfassen.weibliche_platzhalter')}
								</Button>
							</div>
							<div className='d-flex justify-content-around mb-3'>
								<AvatarImage
									key={'id' + 1}
									src={`${process.env.API_URL}v1/glob/files/1/avatar.png`}
									alt={t('nutzerbereich.profilbild.platzhalter')}
									onClick={() => chooseAvatar(1)}
								/>
								<AvatarImage
									key={'id' + '3cc4eeb0-c480-40b9-9b33-7c67bb98bc58'}
									src={`${process.env.API_URL}v1/glob/files/3cc4eeb0-c480-40b9-9b33-7c67bb98bc58/avatar.png`}
									alt={t('nutzerbereich.profilbild.platzhalter')}
									onClick={() =>
										chooseAvatar('3cc4eeb0-c480-40b9-9b33-7c67bb98bc58')
									}
								/>
								<AvatarImage
									key={'id' + '700e8c46-d204-4e49-b92c-6f5f80be0fe3'}
									src={`${process.env.API_URL}v1/glob/files/700e8c46-d204-4e49-b92c-6f5f80be0fe3/avatar.png`}
									alt={t('nutzerbereich.profilbild.platzhalter')}
									onClick={() =>
										chooseAvatar('700e8c46-d204-4e49-b92c-6f5f80be0fe3')
									}
								/>
							</div>
						</div>
					)}
					{sex == 'm' && (
						<AvatarAccordion
							platzhalterString={t('nutzerbereich.profilbild.platzhalter')}
							avatarIds={maleAvatarIds}
							callback={chooseAvatar}
						/>
					)}
					{sex == 'w' && (
						<AvatarAccordion
							platzhalterString={t('nutzerbereich.profilbild.platzhalter')}
							avatarIds={femaleAvatarIds}
							callback={chooseAvatar}
						/>
					)}
				</Modal.Body>
			</Modal>
		</>
	);
};

ChooseAvatarDialog.propTypes = {
	internal: PropTypes.bool || PropTypes.undefined,
	edit: PropTypes.bool || PropTypes.undefined,
	formData: PropTypes.object,
	updateUser: PropTypes.func,
};

export { ChooseAvatarDialog };
